import { useEffect, useRef, useState, type PointerEvent } from "react";
import type { RTKParticipant } from "@cloudflare/realtimekit";
import { MoreVertical, Volume2, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { livestreamAudioSink } from "./livestreamAudioSink";
import { getNameInitials } from "./participantName";

function useParticipantMediaState({ participant }: { participant: RTKParticipant }) {
  const [mediaState, setMediaState] = useState(() => ({
    videoEnabled: participant.videoEnabled,
    screenShareEnabled: participant.screenShareEnabled,
    audioEnabled: participant.audioEnabled,
  }));

  useEffect(() => {
    const sync = () => {
      setMediaState({
        videoEnabled: participant.videoEnabled,
        screenShareEnabled: participant.screenShareEnabled,
        audioEnabled: participant.audioEnabled,
      });
    };

    sync();
    participant.on("videoUpdate", sync);
    participant.on("screenShareUpdate", sync);
    participant.on("audioUpdate", sync);

    return () => {
      participant.off("videoUpdate", sync);
      participant.off("screenShareUpdate", sync);
      participant.off("audioUpdate", sync);
    };
  }, [participant]);

  const hasScreenShare =
    mediaState.screenShareEnabled && Boolean(participant.screenShareTracks?.video);
  const hasCamera = mediaState.videoEnabled && Boolean(participant.videoTrack);
  const hasAudio =
    mediaState.audioEnabled && Boolean(participant.audioTrack);

  return { hasScreenShare, hasCamera, hasAudio };
}

function playMediaElement(el: HTMLMediaElement) {
  void el.play().catch(() => {});
}

function getParticipantAudioTracks({
  participant,
  hasScreenShare,
}: {
  participant: RTKParticipant;
  hasScreenShare: boolean;
}): MediaStreamTrack[] {
  const tracks: MediaStreamTrack[] = [];

  if (
    hasScreenShare &&
    participant.screenShareEnabled &&
    participant.screenShareTracks?.audio
  ) {
    tracks.push(participant.screenShareTracks.audio);
  }

  if (participant.audioEnabled && participant.audioTrack) {
    tracks.push(participant.audioTrack);
  }

  return tracks;
}

const PIP_MARGIN_PX = 12;
const PIP_STACK_BREAKPOINT_PX = 640;

function usePipLayoutEnabled({ enabled }: { enabled: boolean }) {
  const [pipLayoutEnabled, setPipLayoutEnabled] = useState(() => {
    if (!enabled || typeof window === "undefined") return false;
    return window.matchMedia(`(min-width: ${PIP_STACK_BREAKPOINT_PX}px)`).matches;
  });

  useEffect(() => {
    if (!enabled) {
      setPipLayoutEnabled(false);
      return;
    }

    const mediaQuery = window.matchMedia(`(min-width: ${PIP_STACK_BREAKPOINT_PX}px)`);
    const onChange = () => {
      setPipLayoutEnabled(mediaQuery.matches);
    };

    onChange();
    mediaQuery.addEventListener("change", onChange);
    return () => {
      mediaQuery.removeEventListener("change", onChange);
    };
  }, [enabled]);

  return pipLayoutEnabled;
}

function clampPipTranslate({
  x,
  y,
  containerWidth,
  containerHeight,
  pipWidth,
  pipHeight,
}: {
  x: number;
  y: number;
  containerWidth: number;
  containerHeight: number;
  pipWidth: number;
  pipHeight: number;
}) {
  const minX = -(containerWidth - pipWidth - PIP_MARGIN_PX);
  const maxX = 0;
  const minY = 0;
  const maxY = containerHeight - pipHeight - PIP_MARGIN_PX;
  return {
    x: Math.min(maxX, Math.max(minX, x)),
    y: Math.min(maxY, Math.max(minY, y)),
  };
}

function isVideoElementPlaying(el: HTMLVideoElement) {
  return !el.paused && el.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA;
}

export function LivestreamParticipantVideo({
  participant,
  muted = false,
  className,
  fill = false,
  pipDraggable = false,
  showAudioUnmute = false,
  audioUnlocked = true,
  mirrored = false,
}: {
  participant: RTKParticipant;
  muted?: boolean;
  className?: string;
  fill?: boolean;
  pipDraggable?: boolean;
  showAudioUnmute?: boolean;
  audioUnlocked?: boolean;
  mirrored?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const screenShareVideoRef = useRef<HTMLVideoElement>(null);
  const cameraVideoRef = useRef<HTMLVideoElement>(null);
  const remoteAudioRef = useRef<HTMLAudioElement>(null);
  const pipDragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);
  const [pipTranslate, setPipTranslate] = useState({ x: 0, y: 0 });
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [trackMuted, setTrackMuted] = useState(false);
  const name = participant.name?.trim() ?? "";
  const { hasScreenShare, hasCamera, hasAudio } = useParticipantMediaState({
    participant,
  });
  const hasVideo = hasScreenShare || hasCamera;
  const showBoth = hasScreenShare && hasCamera;
  const pipLayoutEnabled = usePipLayoutEnabled({ enabled: pipDraggable });
  const showStackedHostVideo = showBoth && pipDraggable && !pipLayoutEnabled;
  const showDraggablePip = showBoth && pipDraggable && pipLayoutEnabled;

  useEffect(() => {
    if (!showBoth || showStackedHostVideo) {
      setPipTranslate({ x: 0, y: 0 });
      pipDragRef.current = null;
    }
  }, [showBoth, showStackedHostVideo]);

  useEffect(() => {
    const elements = [
      screenShareVideoRef.current,
      cameraVideoRef.current,
    ].filter((el): el is HTMLVideoElement => el != null);

    if (elements.length === 0) {
      setVideoPlaying(false);
      return;
    }

    const syncPlaying = () => {
      setVideoPlaying(elements.some(isVideoElementPlaying));
    };

    for (const el of elements) {
      el.addEventListener("playing", syncPlaying);
      el.addEventListener("pause", syncPlaying);
      el.addEventListener("emptied", syncPlaying);
      el.addEventListener("waiting", syncPlaying);
    }
    syncPlaying();

    return () => {
      for (const el of elements) {
        el.removeEventListener("playing", syncPlaying);
        el.removeEventListener("pause", syncPlaying);
        el.removeEventListener("emptied", syncPlaying);
        el.removeEventListener("waiting", syncPlaying);
      }
    };
  }, [hasScreenShare, hasCamera, participant]);

  const updatePipTranslateFromPointer = ({
    clientX,
    clientY,
  }: {
    clientX: number;
    clientY: number;
  }) => {
    const drag = pipDragRef.current;
    const container = containerRef.current;
    const pip = cameraVideoRef.current;
    if (!drag || !container || !pip) return;

    const dx = clientX - drag.startX;
    const dy = clientY - drag.startY;
    setPipTranslate(
      clampPipTranslate({
        x: drag.originX + dx,
        y: drag.originY + dy,
        containerWidth: container.clientWidth,
        containerHeight: container.clientHeight,
        pipWidth: pip.offsetWidth,
        pipHeight: pip.offsetHeight,
      }),
    );
  };

  const handlePipPointerDown = (event: PointerEvent<HTMLVideoElement>) => {
    if (!showDraggablePip) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    pipDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: pipTranslate.x,
      originY: pipTranslate.y,
    };
  };

  const handlePipPointerMove = (event: PointerEvent<HTMLVideoElement>) => {
    if (pipDragRef.current?.pointerId !== event.pointerId) return;
    updatePipTranslateFromPointer({
      clientX: event.clientX,
      clientY: event.clientY,
    });
  };

  const endPipDrag = (event: PointerEvent<HTMLVideoElement>) => {
    if (pipDragRef.current?.pointerId !== event.pointerId) return;
    pipDragRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  useEffect(() => {
    const el = screenShareVideoRef.current;
    if (!el || !hasScreenShare) return;

    const updateStream = () => {
      const videoTracks: MediaStreamTrack[] = [];

      if (participant.screenShareEnabled && participant.screenShareTracks?.video) {
        videoTracks.push(participant.screenShareTracks.video);
      }

      if (videoTracks.length > 0) {
        el.srcObject = new MediaStream(videoTracks);
        playMediaElement(el);
      } else {
        el.srcObject = null;
      }
    };

    updateStream();
    participant.on("screenShareUpdate", updateStream);

    return () => {
      participant.off("screenShareUpdate", updateStream);
      el.srcObject = null;
    };
  }, [participant, hasScreenShare]);

  useEffect(() => {
    const el = cameraVideoRef.current;
    if (!el || !hasCamera) return;

    const updateStream = () => {
      const tracks: MediaStreamTrack[] = [];

      if (participant.videoEnabled && participant.videoTrack) {
        tracks.push(participant.videoTrack);
      }

      if (tracks.length > 0) {
        el.srcObject = new MediaStream(tracks);
        playMediaElement(el);
      } else {
        el.srcObject = null;
      }
    };

    updateStream();
    participant.on("videoUpdate", updateStream);

    return () => {
      participant.off("videoUpdate", updateStream);
      el.srcObject = null;
    };
  }, [participant, hasCamera]);

  useEffect(() => {
    if (muted) return;

    const audioEl = remoteAudioRef.current;
    const participantId = participant.id;

    const updateStream = () => {
      const audioTracks = getParticipantAudioTracks({ participant, hasScreenShare });

      // The audible output comes from the shared audio sink. This element stays
      // muted and only "pumps" the remote WebRTC stream so Web Audio receives it
      // (works around a Chromium bug where a remote stream is silent in Web Audio
      // unless it is also attached to a media element).
      if (audioEl) {
        audioEl.srcObject =
          audioTracks.length > 0 ? new MediaStream(audioTracks) : null;
        if (audioTracks.length > 0) {
          playMediaElement(audioEl);
        }
      }

      livestreamAudioSink.connect({ id: participantId, tracks: audioTracks });
    };

    updateStream();
    participant.on("audioUpdate", updateStream);
    participant.on("screenShareUpdate", updateStream);

    return () => {
      participant.off("audioUpdate", updateStream);
      participant.off("screenShareUpdate", updateStream);
      if (audioEl) audioEl.srcObject = null;
      livestreamAudioSink.disconnect({ id: participantId });
    };
  }, [participant, hasScreenShare, muted]);

  useEffect(() => {
    setTrackMuted(livestreamAudioSink.isMuted({ id: participant.id }));
    return livestreamAudioSink.subscribeMuted((changedId) => {
      if (changedId === participant.id) {
        setTrackMuted(livestreamAudioSink.isMuted({ id: participant.id }));
      }
    });
  }, [participant.id]);

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden bg-black ${showStackedHostVideo ? "flex flex-col" : ""} ${className ?? ""}`}
    >
      {hasScreenShare ? (
        <video
          ref={screenShareVideoRef}
          autoPlay
          playsInline
          muted
          className={
            showStackedHostVideo
              ? "min-h-0 w-full flex-1 object-contain"
              : "h-full w-full object-contain"
          }
        />
      ) : null}
      {hasCamera ? (
        <video
          ref={cameraVideoRef}
          autoPlay
          playsInline
          muted
          style={
            mirrored || showDraggablePip
              ? {
                  transform: [
                    mirrored ? "scaleX(-1)" : "",
                    showDraggablePip
                      ? `translate(${pipTranslate.x}px, ${pipTranslate.y}px)`
                      : "",
                  ]
                    .filter(Boolean)
                    .join(" "),
                }
              : mirrored
                ? { transform: "scaleX(-1)" }
                : undefined
          }
          onPointerDown={showDraggablePip ? handlePipPointerDown : undefined}
          onPointerMove={showDraggablePip ? handlePipPointerMove : undefined}
          onPointerUp={showDraggablePip ? endPipDrag : undefined}
          onPointerCancel={showDraggablePip ? endPipDrag : undefined}
          className={
            showStackedHostVideo
              ? "aspect-video w-full shrink-0 border-t border-white/10 object-cover"
              : showBoth
                ? showDraggablePip
                  ? "absolute top-3 right-3 z-10 aspect-video w-[min(28%,12rem)] cursor-grab touch-none rounded-lg border border-white/20 object-cover shadow-lg active:cursor-grabbing"
                  : "absolute top-3 right-3 z-10 aspect-video w-[min(28%,12rem)] rounded-lg border border-white/20 object-cover shadow-lg"
                : hasVideo
                  ? fill
                    ? "h-full w-full object-cover object-center"
                    : "h-full w-full object-contain"
                  : "pointer-events-none fixed h-px w-px opacity-0"
          }
        />
      ) : null}
      <audio ref={remoteAudioRef} autoPlay playsInline muted className="sr-only" />
      {!muted ? (
        <div className="absolute top-2 right-2 z-30">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="h-8 w-8 rounded-full bg-black/40 text-white hover:bg-black/60 hover:text-white"
                aria-label="Audio options"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[10rem]">
              <DropdownMenuItem
                onSelect={() => {
                  livestreamAudioSink.toggleMuted({ id: participant.id });
                }}
              >
                {trackMuted ? (
                  <>
                    <Volume2 className="mr-2 h-4 w-4" />
                    Unmute audio
                  </>
                ) : (
                  <>
                    <VolumeX className="mr-2 h-4 w-4" />
                    Mute audio
                  </>
                )}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : null}
      {!hasVideo ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className="flex h-24 w-24 items-center justify-center rounded-full bg-zinc-800 text-2xl font-semibold text-white sm:h-32 sm:w-32 sm:text-3xl"
            aria-label={name || "Participant"}
          >
            {getNameInitials({ name })}
          </div>
        </div>
      ) : null}
      {showAudioUnmute && hasVideo && videoPlaying && !audioUnlocked ? (
        <div className="absolute inset-0 z-20 flex items-center justify-center">
          <Button
            type="button"
            size="lg"
            className="h-12 gap-2 border-white/20 bg-black/70 px-6 text-base text-white backdrop-blur-sm hover:bg-black/85 hover:text-white pointer-events-none"
          >
            <Volume2 className="h-5 w-5" aria-hidden />
            Unmute
          </Button>
        </div>
      ) : null}
    </div>
  );
}
